/**
 * DoctorVerificationGate — Wraps doctor-only features.
 * Shows pending/rejected screens when doctor is not fully verified.
 */

import React, { useEffect, useState } from 'react';
import {
  Shield, Clock, CheckCircle, XCircle, Upload,
  FileText, AlertTriangle, Loader2, RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../App';
import { useLanguage } from '../../i18n/LanguageContext';

interface VerificationStatus {
  status: string;
  hasLicense: boolean;
  hasIdDocument: boolean;
  hasProfile: boolean;
  rejectionReason: string | null;
  documentsSubmittedAt: string | null;
  reviewedAt: string | null;
}

interface Props {
  children: React.ReactNode;
}

const DoctorVerificationGate: React.FC<Props> = ({ children }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'https://health.bhabygroup.co.tz/api';
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/users/profile/verification-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setStatus(await res.json());
        }
      } catch {
        // Silent
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-primary-500" />
      </div>
    );
  }

  // Approved — show full content
  if (status?.status === 'approved') {
    return <>{children}</>;
  }

  // Rejected — show rejection screen with resubmit option
  if (status?.status === 'rejected') {
    return (
      <div className="max-w-lg mx-auto mt-8 p-6">
        <div className="card p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-danger-50 flex items-center justify-center mx-auto mb-4">
            <XCircle size={32} className="text-danger-500" />
          </div>
          <h2 className="text-xl font-bold text-text-primary mb-2">{t('doctorVerificationGate.applicationRejected')}</h2>
          <p className="text-sm text-text-muted mb-4">
            {t('doctorVerificationGate.appNotApproved')}
          </p>
          {status.rejectionReason && (
            <div className="p-3 rounded-lg bg-danger-50 border border-danger-100 text-sm text-danger-700 mb-4">
              <strong>{t('doctorVerificationGate.reason')}</strong> {status.rejectionReason}
            </div>
          )}
          <p className="text-sm text-text-muted mb-6">
            {t('doctorVerificationGate.reviewFeedback')}
          </p>
          <Link to="/doctor/profile" className="btn btn-primary">
            <Upload size={16} /> {t('doctorVerificationGate.resubmitDocuments')}
          </Link>
        </div>
      </div>
    );
  }

  // Pending review — show waiting screen
  if (status?.status === 'pending_review') {
    return (
      <div className="max-w-lg mx-auto mt-8 p-6">
        <div className="card p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-warning-50 flex items-center justify-center mx-auto mb-4">
            <Clock size={32} className="text-warning-600" />
          </div>
          <h2 className="text-xl font-bold text-text-primary mb-2">{t('doctorVerificationGate.verificationInProgress')}</h2>
          <p className="text-sm text-text-muted mb-6">
            {t('doctorVerificationGate.docsSubmittedReview')}
          </p>
          <div className="space-y-3 text-left">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-50">
              <CheckCircle size={16} className={status.hasProfile ? 'text-success-500' : 'text-text-muted'} />
              <span className="text-sm">{t('doctorVerificationGate.profileInfo')}</span>
              <span className={`ml-auto text-xs font-medium ${status.hasProfile ? 'text-success-500' : 'text-text-muted'}`}>
                {status.hasProfile ? t('doctorVerificationGate.complete') : t('doctorVerificationGate.pending')}
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-50">
              <CheckCircle size={16} className={status.hasLicense ? 'text-success-500' : 'text-text-muted'} />
              <span className="text-sm">{t('doctorVerificationGate.medicalLicense')}</span>
              <span className={`ml-auto text-xs font-medium ${status.hasLicense ? 'text-success-500' : 'text-text-muted'}`}>
                {status.hasLicense ? t('doctorVerificationGate.uploaded') : t('doctorVerificationGate.pending')}
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-50">
              <CheckCircle size={16} className={status.hasIdDocument ? 'text-success-500' : 'text-text-muted'} />
              <span className="text-sm">{t('doctorVerificationGate.idPassport')}</span>
              <span className={`ml-auto text-xs font-medium ${status.hasIdDocument ? 'text-success-500' : 'text-text-muted'}`}>
                {status.hasIdDocument ? t('doctorVerificationGate.uploaded') : t('doctorVerificationGate.pending')}
              </span>
            </div>
          </div>
          <p className="text-xs text-text-muted mt-6">
            {t('doctorVerificationGate.reviewedWithin')}
          </p>
        </div>
      </div>
    );
  }

  // No profile yet or draft — show setup prompt
  if (!status?.hasProfile || status?.status === 'none' || status?.status === 'draft') {
    return (
      <div className="max-w-lg mx-auto mt-8 p-6">
        <div className="card p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
            <Shield size={32} className="text-primary-500" />
          </div>
          <h2 className="text-xl font-bold text-text-primary mb-2">{t('doctorVerificationGate.completeYourProfile')}</h2>
          <p className="text-sm text-text-muted mb-6">
            {t('doctorVerificationGate.startAccepting')}
          </p>
          <Link to="/doctor/profile" className="btn btn-primary">
            <FileText size={16} /> {t('doctorVerificationGate.completeProfile')}
          </Link>
        </div>
      </div>
    );
  }

  // Default — show content
  return <>{children}</>;
};

export default DoctorVerificationGate;
